import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Paper,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Grid,
  Button,
  IconButton,
  Divider,
  Chip
} from "@mui/material";
import {
  SaveAlt,
  Groups,
  Call as CallIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  GroupAdd as GroupAddIcon,
  Star,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { ChatsUser } from "./ChartsUser";
import ChartDonut from "./ChartDonut";
import { ChartsDate } from "./ChartsDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import usePlans from "../../hooks/usePlans";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import { formatPlanDisplayName } from "../../utils/formatPlanDisplayName";
import api from "../../services/api";

// Tema v4 (whitelabel igual ao login) + tema v5 para demais tokens
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";

// Cartões de estatísticas
const CARD_GRADIENTS = [
  "linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)",
  "linear-gradient(135deg, #00c9a7 0%, #00a389 100%)",
  "linear-gradient(135deg, #1e3a5f 0%, #00c9a7 100%)",
  "linear-gradient(135deg, #2d5a8e 0%, #1e3a5f 100%)",
  "linear-gradient(135deg, #00a389 0%, #1e3a5f 100%)",
  "linear-gradient(135deg, #1e3a5f 0%, #3a7bd5 100%)",
];

const StatCard = ({ title, value, icon, color, index = 0 }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 4,
      background: CARD_GRADIENTS[index % CARD_GRADIENTS.length],
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 8px 32px rgba(30,58,95,0.25)",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
      "&:hover": { transform: "translateY(-6px) scale(1.02)", boxShadow: "0 16px 48px rgba(30,58,95,0.35)" },
      "&::before": {
        content: '""', position: "absolute", top: -20, right: -20,
        width: 100, height: 100, borderRadius: "50%",
        background: "rgba(255,255,255,0.08)",
      },
      "&::after": {
        content: '""', position: "absolute", bottom: -30, left: -30,
        width: 80, height: 80, borderRadius: "50%",
        background: "rgba(255,255,255,0.05)",
      },
    }}
  >
    <CardContent sx={{ position: "relative", zIndex: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box>
          <Typography variant="overline" sx={{ fontWeight: 600, color: "rgba(255,255,255,0.7)", fontSize: "0.7rem", letterSpacing: "0.1em" }}>
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)", color: "#fff", width: 60, height: 60, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          <SvgIcon sx={{ fontSize: 28 }}>{icon}</SvgIcon>
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

// Métricas NPS
const NpsMetricCard = ({ title, value, color }) => (
  <Card sx={{ height: "100%", textAlign: "center", p: 2, borderRadius: 3, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
    <Typography variant="overline" color="text.secondary">{title}</Typography>
    <Typography variant="h3" fontWeight="bold" sx={{ color, my: 1 }}>{value}%</Typography>
    <Box sx={{ height: 8, backgroundColor: "grey.200", borderRadius: 1, overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: `${value}%`, backgroundColor: color }} />
    </Box>
  </Card>
);

// Client-specific dashboard
import ClientDashboard from "./ClientDashboard";

const Dashboard = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4(); // whitelabel (igual à tela de login)

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);
  const { getPlanCompany, getPlanList } = usePlans();

  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [publicPlans, setPublicPlans] = useState([]);
  const [publicPlansLoading, setPublicPlansLoading] = useState(false);

  const [channelsStatus, setChannelsStatus] = useState([]);
  const [channelsStatusLoading, setChannelsStatusLoading] = useState(false);
  const [channelsStatusError, setChannelsStatusError] = useState(null);

  const [companyMetrics, setCompanyMetrics] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    conversionRate: 0,
    avgResponseTime: 0,
    customerSatisfaction: 0,
    activeUsers: 0,
    newLeads: 0,
    closedDeals: 0,
  });
  const [companyMetricsLoading, setCompanyMetricsLoading] = useState(false);
  const [companyMetricsError, setCompanyMetricsError] = useState(null);
  const [analyticsModuleDisabled, setAnalyticsModuleDisabled] = useState(false);

  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(false);
  const [recentActivitiesError, setRecentActivitiesError] = useState(null);

  const showMembershipCard = ["cliente", "supervisor", "vendedor"].includes(user?.profile);

  const membershipLicenseText = useMemo(() => {
    const dueDateIso = user?.company?.dueDate;
    if (!dueDateIso) return i18n.t("dashboard.membership.license.noExpiration");

    const due = moment(dueDateIso);
    if (!due.isValid()) return i18n.t("dashboard.membership.license.noExpiration");
    if (due.year() >= 2100) return i18n.t("dashboard.membership.license.noExpiration");

    const today = moment().startOf("day");
    const end = due.startOf("day");
    const diffDays = end.diff(today, "days");

    const endText = end.toDate().toLocaleDateString(i18n.language);

    if (diffDays === 0) return i18n.t("dashboard.membership.license.expiresToday");
    if (diffDays < 0) return i18n.t("dashboard.membership.license.expiredOn", { date: endText });
    return i18n.t("dashboard.membership.license.expiresOn", { date: endText });
  }, [user?.company?.dueDate, i18n.language]);

  useEffect(() => {
    const fetchMembership = async () => {
      if (!showMembershipCard || !user?.companyId) return;
      setMembershipLoading(true);
      setPublicPlansLoading(true);
      try {
        const [companyPlanRes, plansRes] = await Promise.allSettled([
          getPlanCompany(undefined, user.companyId),
          getPlanList(),
        ]);

        if (companyPlanRes.status === "fulfilled") {
          setMembership(companyPlanRes.value || null);
        } else {
          setMembership(null);
        }

        if (plansRes.status === "fulfilled") {
          setPublicPlans(Array.isArray(plansRes.value) ? plansRes.value : []);
        } else {
          setPublicPlans([]);
        }
      } catch (error) {
        // Do not block dashboard if membership fetch fails.
        setMembership(null);
        setPublicPlans([]);
      } finally {
        setMembershipLoading(false);
        setPublicPlansLoading(false);
      }
    };

    fetchMembership();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMembershipCard, user?.companyId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
          date_to: new Date().toISOString().slice(0, 10)
        };
        const data = await find(params);
        setCounters(data.counters);
        if (isArray(data.attendants)) setAttendants(data.attendants);
      } catch (error) {
        toast.error(i18n.t("dashboard.errors.loadData"));
        console.error(error);
        // Fallback honesto (sin datos inventados) - REGLA_CODE_20260118_DASHBOARDS
        setCounters({
          supportHappening: 0,
          supportPending: 0,
          supportFinished: 0,
          supportGroups: 0,
          leads: 0,
        });
        setAttendants([]);
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isModuleDisabledError = (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const errorCode = typeof detail === "object" ? detail?.error : null;
      return status === 403 && errorCode === "module_disabled";
    };

    const fetchCompanyInsights = async () => {
      if (!user?.companyId) return;

      // Defaults: current month -> today (same pattern as dashboard counters)
      const date_from = moment().startOf("month").format("YYYY-MM-DD");
      const date_to = moment().format("YYYY-MM-DD");

      setChannelsStatusLoading(true);
      setChannelsStatusError(null);

      setCompanyMetricsLoading(true);
      setCompanyMetricsError(null);

      setRecentActivitiesLoading(true);
      setRecentActivitiesError(null);

      setAnalyticsModuleDisabled(false);

      try {
        const [channelsRes, metricsRes, activitiesRes] = await Promise.allSettled([
          api.get("/channels/status"),
          api.get("/analytics/metrics", { params: { date_from, date_to } }),
          api.get("/activities/recent", { params: { limit: 8 } }),
        ]);

        if (channelsRes.status === "fulfilled") {
          const channels = channelsRes.value?.data?.channels;
          setChannelsStatus(Array.isArray(channels) ? channels : []);
        } else {
          setChannelsStatus([]);
          setChannelsStatusError("dashboard.channels.errors.load");
          console.error(channelsRes.reason);
        }

        const moduleDisabled =
          (metricsRes.status === "rejected" && isModuleDisabledError(metricsRes.reason)) ||
          (activitiesRes.status === "rejected" && isModuleDisabledError(activitiesRes.reason));

        if (moduleDisabled) {
          setAnalyticsModuleDisabled(true);
          setCompanyMetrics({
            totalRevenue: 0,
            monthlyRevenue: 0,
            conversionRate: 0,
            avgResponseTime: 0,
            customerSatisfaction: 0,
            activeUsers: 0,
            newLeads: 0,
            closedDeals: 0,
          });
          setRecentActivities([]);
          return;
        }

        if (metricsRes.status === "fulfilled") {
          const m = metricsRes.value?.data || {};
          setCompanyMetrics({
            totalRevenue: Number(m.totalRevenue || 0),
            monthlyRevenue: Number(m.monthlyRevenue || 0),
            conversionRate: Number(m.conversionRate || 0),
            avgResponseTime: Number(m.avgResponseTime || 0),
            customerSatisfaction: Number(m.customerSatisfaction || 0),
            activeUsers: Number(m.activeUsers || 0),
            newLeads: Number(m.newLeads || 0),
            closedDeals: Number(m.closedDeals || 0),
          });
        } else {
          setCompanyMetrics({
            totalRevenue: 0,
            monthlyRevenue: 0,
            conversionRate: 0,
            avgResponseTime: 0,
            customerSatisfaction: 0,
            activeUsers: 0,
            newLeads: 0,
            closedDeals: 0,
          });
          setCompanyMetricsError("dashboard.analytics.errors.metrics");
          console.error(metricsRes.reason);
        }

        if (activitiesRes.status === "fulfilled") {
          const activities = activitiesRes.value?.data;
          setRecentActivities(Array.isArray(activities) ? activities : []);
        } else {
          setRecentActivities([]);
          setRecentActivitiesError("dashboard.analytics.errors.activity");
          console.error(activitiesRes.reason);
        }
      } catch (err) {
        console.error(err);
        setChannelsStatus([]);
        setChannelsStatusError("dashboard.channels.errors.load");

        setCompanyMetrics({
          totalRevenue: 0,
          monthlyRevenue: 0,
          conversionRate: 0,
          avgResponseTime: 0,
          customerSatisfaction: 0,
          activeUsers: 0,
          newLeads: 0,
          closedDeals: 0,
        });
        setCompanyMetricsError("dashboard.analytics.errors.metrics");

        setRecentActivities([]);
        setRecentActivitiesError("dashboard.analytics.errors.activity");
      } finally {
        setChannelsStatusLoading(false);
        setCompanyMetricsLoading(false);
        setRecentActivitiesLoading(false);
      }
    };

    fetchCompanyInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const exportToExcel = () => {
    try {
      const table = document.getElementById("grid-attendants");
      const ws = XLSX.utils.table_to_sheet(table);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, i18n.t("dashboard.export.agentsSheet"));
      XLSX.writeFile(wb, i18n.t("dashboard.export.agentsFile"));
    } catch {
      toast.error(i18n.t("dashboard.export.error"));
    }
  };

  const getOnlineUsersCount = () => Array.isArray(attendants) ? attendants.filter(u => u.online).length : 0;

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const statCards = [
    { title: i18n.t("dashboard.cards.inAttendance"), value: counters?.supportHappening || 0, icon: <CallIcon />, color: PRIMARY_MAIN },
    { title: i18n.t("dashboard.cards.waiting"), value: counters?.supportPending || 0, icon: <HourglassEmptyIcon />, color: themeV5.palette.info.main },
    { title: i18n.t("dashboard.cards.finalized"), value: counters?.supportFinished || 0, icon: <CheckCircleIcon />, color: themeV5.palette.success.main },
    { title: i18n.t("dashboard.cards.groups"), value: counters?.supportGroups || 0, icon: <Groups />, color: themeV5.palette.secondary.main },
    { title: i18n.t("dashboard.cards.activeAttendants"), value: `${getOnlineUsersCount()}/${Array.isArray(attendants) ? attendants.length : 0}`, icon: <RecordVoiceOverIcon />, color: themeV5.palette.error.main },
    { title: i18n.t("dashboard.cards.newContacts"), value: counters?.leads || 0, icon: <GroupAddIcon />, color: themeV5.palette.warning.main }
  ];

  const npsData = {
    score: counters.npsScore || 0,
    promoters: counters.npsPromotersPerc || 0,
    passives: counters.npsPassivePerc || 0,
    detractors: counters.npsDetractorsPerc || 0,
    totalTickets: counters.tickets || 0,
    withRating: counters.withRating || 0,
    percRating: counters.percRating || 0
  };

  const npsColors = { promoters: "#2EA85A", passives: "#F7EC2C", detractors: "#F73A2C" };
  const npsChartData = [
    { name: i18n.t("dashboard.assessments.prosecutors"), value: npsData.promoters },
    { name: i18n.t("dashboard.assessments.neutral"), value: npsData.passives },
    { name: i18n.t("dashboard.assessments.detractors"), value: npsData.detractors }
  ];
  const sortedNpsColors = [npsColors.promoters, npsColors.passives, npsColors.detractors];

  const currentPlanId = String(membership?.plan?.id || membership?.planId || "");
  const currentPlanAmount = Number(membership?.plan?.amount || membership?.amount || 0);

  const sortedPublicPlans = Array.isArray(publicPlans)
    ? [...publicPlans].sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0))
    : [];

  const numberLocale = (() => {
    const l = String(i18n.language || "").toLowerCase();
    if (l.startsWith("pt")) return "pt-BR";
    if (l.startsWith("en")) return "en-US";
    if (l.startsWith("fr")) return "fr-FR";
    if (l.startsWith("it")) return "it-IT";
    return "es-ES";
  })();

  const momentLocale = (() => {
    const l = String(i18n.language || "").toLowerCase();
    if (l.startsWith("pt")) return "pt-br";
    if (l.startsWith("en")) return "en";
    if (l.startsWith("fr")) return "fr";
    if (l.startsWith("it")) return "it";
    return "es";
  })();

  const freeLabel = i18n.t("subscription.free");

  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    if (!Number.isFinite(n) || n <= 0) return freeLabel;
    return n.toLocaleString(numberLocale, { minimumFractionDigits: 2 });
  };

  const statusChip = (status) => {
    const st = String(status || "").toLowerCase();
    if (st === "connected" || st === "active") return { label: i18n.t("dashboard.channels.status.connected"), color: "success" };
    if (st === "error") return { label: i18n.t("dashboard.channels.status.error"), color: "error" };
    return { label: i18n.t("dashboard.channels.status.disconnected"), color: "default" };
  };

  const formatMoney = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return (0).toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n.toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatMinutes = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return "0";
    return n.toLocaleString(numberLocale, { maximumFractionDigits: 2 });
  };

  const metricsDateFromText = moment().startOf("month").toDate().toLocaleDateString(i18n.language);
  const metricsDateToText = moment().toDate().toLocaleDateString(i18n.language);

  // Client profile gets a simplified dashboard
  const isClient = String(user?.profile || "").toLowerCase() === "cliente";
  if (isClient) return <ClientDashboard />;

  return (
    <Box sx={{ background: "linear-gradient(180deg, #f0f4f8 0%, #e8eef5 100%)", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Título com cor do whitelabel */}
        <Box sx={{ mb: 4, p: 3, borderRadius: 4, background: "linear-gradient(135deg, #1e3a5f 0%, #00c9a7 100%)", color: "#fff", position: "relative", overflow: "hidden", boxShadow: "0 8px 32px rgba(30,58,95,0.2)" }}>
          <Box sx={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
          <Box sx={{ position: "absolute", bottom: -20, left: "40%", width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
          <Typography variant="h4" fontWeight={800} data-testid="dashboard-title" sx={{ position: "relative", zIndex: 1, textShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {i18n.t("dashboard.title") || "Panel de Control"}
          </Typography>
          <Typography variant="body2" sx={{ position: "relative", zIndex: 1, opacity: 0.8, mt: 0.5 }}>
            Bienvenido de vuelta. Aqui tienes el resumen de tu negocio.
          </Typography>
        </Box>

        {showMembershipCard && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={2}
                  >
                    <Box>
                      <Typography variant="overline" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        {i18n.t("dashboard.membership.title")}
                      </Typography>
                       <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
                        {membershipLoading ? i18n.t("common.loading") : formatPlanDisplayName(membership?.plan, i18n.language)}
                      </Typography>
                      {membership?.plan && (
                        <Typography variant="body2" color="text.secondary">
                          {i18n.t("subscription.fields.users")}: {membership.plan.users ?? "-"} |{" "}
                          {i18n.t("subscription.fields.connections")}: {membership.plan.connections ?? "-"} |{" "}
                          {i18n.t("subscription.fields.queues")}: {membership.plan.queues ?? "-"}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {i18n.t("subscription.fields.dueDate")}: {membershipLicenseText}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        component={RouterLink}
                        to="/subscription"
                        variant="contained"
                        sx={{
                          bgcolor: PRIMARY_MAIN,
                          color: PRIMARY_CONTRAST,
                          "&:hover": { bgcolor: PRIMARY_DARK },
                        }}
                      >
                        {i18n.t("dashboard.membership.details")}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {showMembershipCard && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                {i18n.t("dashboard.membership.availablePlansTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {i18n.t("dashboard.membership.availablePlansSubtitle")}
              </Typography>
            </Grid>

            {publicPlansLoading && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t("dashboard.membership.loadingPlans")}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {!publicPlansLoading && sortedPublicPlans.length === 0 && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t("dashboard.membership.noPublicPlans")}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {!publicPlansLoading &&
              sortedPublicPlans.map((p) => {
                const planId = String(p?.id || "");
                const isCurrent = !!planId && !!currentPlanId && planId === currentPlanId;
                const amount = Number(p?.amount || 0);
                const isUpgrade = amount > currentPlanAmount;

                const channels = [];
                if (p?.useWhatsapp) channels.push("WhatsApp");
                if (p?.useFacebook) channels.push("Facebook");
                if (p?.useInstagram) channels.push("Instagram");

                return (
                  <Grid item xs={12} sm={6} md={3} key={planId || p?.name}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        border: "2px solid",
                        borderColor: isCurrent ? PRIMARY_MAIN : "transparent",
                        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Typography variant="overline" sx={{ fontWeight: 700, color: "text.secondary" }}>
                              {i18n.t("dashboard.membership.planLabel")}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: "text.primary" }}>
                              {formatPlanDisplayName(p, i18n.language)}
                            </Typography>
                          </Box>
                          {isCurrent && (
                            <Chip
                              label={i18n.t("dashboard.membership.activeBadge")}
                              size="small"
                              sx={{
                                bgcolor: PRIMARY_MAIN,
                                color: PRIMARY_CONTRAST,
                                fontWeight: 800,
                              }}
                            />
                          )}
                        </Stack>

                        <Typography variant="h4" sx={{ mt: 1, fontWeight: 900, color: PRIMARY_MAIN }}>
                          {formatAmount(amount)}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" color="text.secondary">
                          {i18n.t("subscription.fields.users")}: <b>{p?.users ?? "-"}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {i18n.t("subscription.fields.connections")}: <b>{p?.connections ?? "-"}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {i18n.t("subscription.fields.queues")}: <b>{p?.queues ?? "-"}</b>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {i18n.t("dashboard.membership.channelsLabel")}: <b>{channels.length ? channels.join(", ") : "-"}</b>
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Button
                            component={RouterLink}
                            to="/subscription"
                            variant={isCurrent ? "outlined" : "contained"}
                            disabled={isCurrent}
                            sx={{
                              flex: 1,
                              bgcolor: isCurrent ? "transparent" : PRIMARY_MAIN,
                              color: isCurrent ? PRIMARY_MAIN : PRIMARY_CONTRAST,
                              borderColor: PRIMARY_MAIN,
                              "&:hover": {
                                bgcolor: isCurrent ? "transparent" : PRIMARY_DARK,
                              },
                            }}
                          >
                            {isCurrent
                              ? i18n.t("dashboard.membership.buttons.current")
                              : isUpgrade
                              ? i18n.t("dashboard.membership.buttons.upgrade")
                              : i18n.t("dashboard.membership.buttons.view")}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <StatCard {...card} index={index} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="overline" sx={{ fontWeight: 700, color: "text.secondary" }}>
                      {i18n.t("dashboard.channels.titleOverline")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: "text.primary" }}>
                      {i18n.t("dashboard.channels.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {i18n.t("dashboard.channels.subtitle")}
                    </Typography>
                  </Box>
                  <Button
                    component={RouterLink}
                    to="/config-apis"
                    variant="contained"
                    sx={{
                      bgcolor: PRIMARY_MAIN,
                      color: PRIMARY_CONTRAST,
                      "&:hover": { bgcolor: PRIMARY_DARK },
                    }}
                  >
                    {i18n.t("dashboard.channels.configureApis")}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {channelsStatusLoading && (
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t("dashboard.channels.loading")}
                  </Typography>
                )}

                {!channelsStatusLoading && Array.isArray(channelsStatus) && channelsStatus.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {i18n.t("dashboard.channels.empty")}
                  </Typography>
                )}

                {!channelsStatusLoading && Array.isArray(channelsStatus) && channelsStatus.length > 0 && (
                  <Stack spacing={1.5}>
                    {channelsStatus.map((ch, idx) => {
                      const chip = statusChip(ch?.status);
                      const channelKey = String(ch?.channel || ch?.name || idx);
                      const lastSync = ch?.lastSync ? moment(ch.lastSync).locale(momentLocale) : null;
                      const lastSyncText = lastSync && lastSync.isValid()
                        ? lastSync.toDate().toLocaleString(i18n.language)
                        : "-";

                      return (
                        <Stack
                          key={channelKey}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={2}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {ch?.name || ch?.channel || "-"}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {lastSyncText}
                            </Typography>
                            <Chip size="small" label={chip.label} color={chip.color} />
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}

                {channelsStatusError && (
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    {i18n.t(channelsStatusError)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="overline" sx={{ fontWeight: 700, color: "text.secondary" }}>
                      {i18n.t("common.company")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: "text.primary" }}>
                      {i18n.t("dashboard.analytics.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {i18n.t("dashboard.analytics.period", { from: metricsDateFromText, to: metricsDateToText })}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={
                      analyticsModuleDisabled
                        ? i18n.t("dashboard.analytics.chips.unavailable")
                        : i18n.t("dashboard.analytics.chips.realData")
                    }
                    color={analyticsModuleDisabled ? "default" : "success"}
                    variant={analyticsModuleDisabled ? "outlined" : "filled"}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                {analyticsModuleDisabled && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {i18n.t("dashboard.analytics.disabledMessage")}
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/subscription"
                      variant="contained"
                      sx={{
                        bgcolor: PRIMARY_MAIN,
                        color: PRIMARY_CONTRAST,
                        "&:hover": { bgcolor: PRIMARY_DARK },
                      }}
                    >
                      {i18n.t("configApis.empty.button")}
                    </Button>
                  </Box>
                )}

                {!analyticsModuleDisabled && (
                  <>
                    {(companyMetricsLoading || recentActivitiesLoading) && (
                      <Typography variant="body2" color="text.secondary">
                        {i18n.t("dashboard.analytics.loading")}
                      </Typography>
                    )}

                    {!companyMetricsLoading && (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.monthlyRevenue")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatMoney(companyMetrics.monthlyRevenue)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.totalRevenue")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatMoney(companyMetrics.totalRevenue)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.conversion")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            {Number(companyMetrics.conversionRate || 0).toLocaleString(numberLocale, { maximumFractionDigits: 2 })}%
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.firstResponse")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatMinutes(companyMetrics.avgResponseTime)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.activeUsers")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{Number(companyMetrics.activeUsers || 0)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.newLeads")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{Number(companyMetrics.newLeads || 0)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">{i18n.t("dashboard.analytics.metrics.closings")}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 900 }}>{Number(companyMetrics.closedDeals || 0)}</Typography>
                        </Grid>
                      </Grid>
                    )}

                    {companyMetricsError && (
                      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {i18n.t(companyMetricsError)}
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                      {i18n.t("dashboard.analytics.activity.title")}
                    </Typography>

                    {recentActivitiesLoading && (
                      <Typography variant="body2" color="text.secondary">
                        {i18n.t("dashboard.analytics.activity.loading")}
                      </Typography>
                    )}

                    {!recentActivitiesLoading && Array.isArray(recentActivities) && recentActivities.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {i18n.t("dashboard.analytics.activity.empty")}
                      </Typography>
                    )}

                    {!recentActivitiesLoading && Array.isArray(recentActivities) && recentActivities.length > 0 && (
                      <Stack spacing={1}>
                        {recentActivities.slice(0, 6).map((a) => {
                          const created = a?.createdAt ? moment(a.createdAt) : null;
                          const createdText = created && created.isValid() ? created.locale(momentLocale).fromNow() : "";
                          const title = a?.subject || a?.type || i18n.t("dashboard.analytics.activity.fallbackTitle");
                          const desc = a?.description || a?.status || "";

                          return (
                            <Box
                              key={String(a?.id || `${title}-${createdText}`)}
                              sx={{ p: 1.2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {createdText}
                                </Typography>
                              </Stack>
                              {desc && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  {desc}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    )}

                    {recentActivitiesError && (
                      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {i18n.t(recentActivitiesError)}
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, bgcolor: "transparent" }}>
          <Tabs
            value={activeTab}
            onChange={(e, nv) => setActiveTab(nv)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                fontWeight: 700,
                textTransform: "uppercase", // ← deixar como NPS
                fontSize: "0.95rem",
                color: "text.secondary",
                "&.Mui-selected": { color: PRIMARY_MAIN }
              },
              "& .MuiTabs-indicator": { backgroundColor: PRIMARY_MAIN, height: 3, borderRadius: "2px" }
            }}
          >
            <Tab label={i18n.t("dashboard.tabs.performance")} />
            <Tab label={i18n.t("dashboard.tabs.assessments")} />
            <Tab label={i18n.t("dashboard.tabs.attendants")} />
          </Tabs>
        </Paper>

        <Box>
          {activeTab === 0 && (
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
              <ChartsDate />
            </Paper>
          )}

          {activeTab === 1 && (
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "primary.light", color: "primary.main", mr: 2 }}>
                  <Star />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">{i18n.t("dashboard.tabs.assessments")}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                    <Typography variant="overline" color="text.secondary">{i18n.t("dashboard.assessments.score")}</Typography>
                    <ChartDonut data={npsChartData} value={npsData.score} colors={sortedNpsColors} />
                  </Card>
                </Grid>

                <Grid item container xs={12} md={8} spacing={2}>
                  <NpsMetricCard title={i18n.t("dashboard.assessments.prosecutors")} value={npsData.promoters} color={npsColors.promoters} />
                  <NpsMetricCard title={i18n.t("dashboard.assessments.neutral")} value={npsData.passives} color={npsColors.passives} />
                  <NpsMetricCard title={i18n.t("dashboard.assessments.detractors")} value={npsData.detractors} color={npsColors.detractors} />
                </Grid>

                <Grid item xs={12} mt={2}>
                  <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                    <Grid container spacing={2} textAlign="center">
                      {[
                        { title: i18n.t("dashboard.assessments.totalCalls"), value: npsData.totalTickets },
                        { title: i18n.t("dashboard.assessments.ratedCalls"), value: npsData.withRating },
                        { title: i18n.t("dashboard.assessments.evaluationIndex"), value: `${npsData.percRating.toFixed(1)}%` }
                      ].map((item, index) => (
                        <Grid item xs={12} sm={4} key={item.title} sx={{ p: 2, position: "relative" }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>{item.title}</Typography>
                          <Typography variant="h5" fontWeight="bold" sx={{ color: PRIMARY_MAIN }}>{item.value}</Typography>
                          {index < 2 && <Divider orientation="vertical" flexItem sx={{ position: "absolute", right: 0, top: "15%", height: "70%" }} />}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )}

          {activeTab === 2 && (
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">{i18n.t("dashboard.tabs.attendants")}</Typography>
                <IconButton
                  onClick={exportToExcel}
                  size="small"
                  sx={{
                    backgroundColor: PRIMARY_MAIN,
                    color: PRIMARY_CONTRAST,
                    transition: "all .2s ease-in-out",
                    "&:hover": { backgroundColor: PRIMARY_DARK, transform: "translateY(-1px)", boxShadow: "0 6px 18px rgba(0,0,0,.15)" }
                  }}
                >
                  <SaveAlt />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <div id="grid-attendants">
                {Array.isArray(attendants) && attendants.length > 0 && <TableAttendantsStatus attendants={attendants} loading={loading} />}
              </div>
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{i18n.t("dashboard.charts.userPerformance")}</Typography>
                <Divider sx={{ mb: 3 }} />
                <ChatsUser />
              </Box>
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
